import {Checkbox, Col, DatePicker, InputNumber, Layout, message, Row, Select, Space, Typography} from 'antd';
import { Button, Spin } from 'antd';
import moment, { Moment } from 'moment';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo, useRef,
  useState,
} from 'react';
import { isEmpty } from 'underscore';
import RoseChart from '../../../../components/Chart/RoseChartPlotly';
import { Label } from '../../../../components/Label/Label';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import { IPlotVariable, RosePlotChart } from '../../../../typings/analytics';
import styles from '../../Style/LayoutPanel.module.scss';
import { ChartBox } from '../ChartBox';
import { IBestDataset } from '../../../../typings/analytics';
import { IGetRosePlotV2ApiPayload } from '../../../../typings/chart';
import { formatDate } from '../../../../generic/common';
import { StoreContext } from '../../../../store/store';
import { getPointNameAndAcronym } from '../AnalyticsPanel';
import { SplitCoordinates } from '../../../../generic/coordinate';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import { IDatasetBrief } from '../../../../typings/metadata';
import CustomDivider from '../CustomDIvider';
import IconPoint from '../../../../assets/icon/locate2.png'
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function RosePlot(props: {
  onDatasetChange: (item: string) => void
  previewDatasetId: string;
  previewDatasetTimeRange: [moment.Moment, moment.Moment];
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  datasets: IBestDataset[];
  defaultDirectionShortName: string;
  defaultValueShortName: string;
  variables: IPlotVariable[];
  dateRange: string[];
  plotData: RosePlotChart;
  plotDefaultInterval: number;
  updatedRosePlotData: RosePlotChart | null;
  pointProps: string;
  pointName: string;
  userInput: IAnalytcisUserInput;
  listNavigation: ReactNode
  errorUpdate: string
  updating: boolean
  point?: MarkerItem
  onUpdateRosePlot: (values: IGetRosePlotV2ApiPayload) => void;
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  setUpdatedRosePlotData: (item: any) => void
}) {
  const {
    userInput,
    updateUserInput,
    setUpdatedRosePlotData,
    onDatasetChange,
    listNavigation,
    errorUpdate,
    updating,
    point,
    pointName,
    isForAllAnalytics,
    setGlobalDateRange,
    setIsForAllAnalytics,
    globalDateRange
  } = props;

  const { datasetBriefs } = useContext(StoreContext);

  const degreeIntervals = [4, 8, 12, 16, 32, 36];
  const [rosePlotData, setRosePlotData] = useState<any[]>([]);
  const [rosePlotSeries, setRosePlotSeries] = useState<any[]>([]);
  const [dataset, setDataset] = useState(userInput.RosePlot.dataset);
  const [variable, setVariable] = useState<IPlotVariable | null>(
    userInput.RosePlot.variable
  );
  const [direction, setDirection] = useState<IPlotVariable | null>(
    userInput.RosePlot.direction
  );
  const [variables, setVariables] = useState<IPlotVariable[]>([]);
  const [directions, setDirections] = useState<IPlotVariable[]>([]);
  const [legendInterval, setLegendInterval] = useState(
    userInput.RosePlot.legendInterval
  );
  const [directionNo, setDirectionNo] = useState(
    userInput.RosePlot.directionNo
  );
  const [directionChartNo, setDirectionChartNo] = useState(16);
  const [month, setMonth] = useState(userInput.RosePlot.month);
  const [startDate, setStartDate] = useState(
    userInput.RosePlot.startDate
      ? userInput.RosePlot.startDate
      : moment(props.previewDatasetTimeRange[1]).subtract(1, 'years')
  );
  const [endDate, setEndDate] = useState(
    userInput.RosePlot.endDate
      ? userInput.RosePlot.endDate
      : props.previewDatasetTimeRange[1]
  );
  const [unitX, setUnitX] = useState('');
  const [previewsettingChanged, setPreviewsettingChanged] = useState(false);
  const selfTriggered = useRef(false);

  useEffect(() => {
    updateUserInput({
      RosePlot: {
        dataset: dataset,
        variable: variable,
        startDate: startDate,
        endDate: endDate,
        direction: direction,
        month: month,
        directionNo: directionNo,
        legendInterval: legendInterval,
      },
    });
  }, [
    dataset,
    variable,
    startDate,
    endDate,
    direction,
    month,
    directionNo,
    legendInterval,
  ]);

  const doStartDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setStartDate(value);
      if(isForAllAnalytics){
        setGlobalDateRange([value, endDate])
      }
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setPreviewsettingChanged(true);
      setEndDate(value);
      if(isForAllAnalytics){
        setGlobalDateRange([startDate, value])
      }
    }
  };

  const doChangeDirectionNo = (active: number | string | undefined) => {
    if (active !== undefined && !isNaN(Number(active))) {
      selfTriggered.current = true
      setPreviewsettingChanged(true);
      setDirectionNo(Number(active));
    }
  };

  const doChangeIntervalNo = (active: number | string | null | undefined) => {
    if (active !== undefined && !isNaN(Number(active))) {
      selfTriggered.current = true
      setPreviewsettingChanged(true);
      setLegendInterval(Number(active));
    }
  };

  const handleCheckboxChange = () => {
    selfTriggered.current = true;
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics);
  };

  const doVariableChange = (active: number) => {
    if (variables.length > 0) {
      selfTriggered.current = true
      const item = props.variables.find((a) => a.Index === active)!;
      setPreviewsettingChanged(true);
      setVariable(item);
    }
  };

  const doDirectionChange = (active: number) => {
    if (directions.length > 0) {
      selfTriggered.current = true
      const item = props.variables.find((a) => a.Index === active)!;
      setPreviewsettingChanged(true);
      setDirection(item);
    }
  };

  const [downloadName, setDownloadName] = useState<string | undefined>();

  useEffect(() => {
    if (
      !previewsettingChanged &&
      props.dateRange &&
      props.dateRange.length > 1
    ) {
      setDownloadName(
        getPointNameAndAcronym(props.previewDatasetId, datasetBriefs, point) +
        props.pointProps +
        '_' +
        formatDate(moment(props.dateRange[0])) +
        '_' +
        formatDate(moment(props.dateRange[1])) +
        '_Rose_Plot'
      );
    }
  }, [
    props.dateRange,
    previewsettingChanged,
    props.pointProps,
    props.previewDatasetId,
    datasetBriefs,
    point && point.label,
  ]);

  useEffect(() => {
    if (!dataset && !isEmpty(props.datasets)) {
      setDataset(props.previewDatasetId);
    }
  }, [dataset, props.datasets, props.previewDatasetId]);

  useEffect(() => {
    if (!isEmpty(props.variables)) {
      const variableList = props.variables.filter(
        (item) => item.Name !== 'Time' && !item.Name.includes('Direction') && !item.Name.includes('Water Level')
      );
      const directionsList = props.variables.filter(
        (item) => item.Name !== 'Time' && item.Name.includes('Direction') && !item.Name.includes('Water Level')
      );
      setVariables(variableList);
      setDirections(directionsList);
      if (
        (!variable || !direction) &&
        props.defaultValueShortName &&
        props.defaultDirectionShortName &&
        variableList.length > 0 &&
        directionsList.length > 0
      ) {
        const defaultVariableX = variableList.find(
          (v: IPlotVariable) => v.ShortName === props.defaultValueShortName
        );
        defaultVariableX !== undefined
          ? setVariable(defaultVariableX)
          : setVariable(variableList[0]);

        const defaultVariableY = directionsList.find(
          (v: IPlotVariable) => v.ShortName === props.defaultDirectionShortName
        );
        defaultVariableY !== undefined
          ? setDirection(defaultVariableY)
          : setDirection(directionsList[0]);
      }
    }
  }, [
    variable,
    direction,
    props.variables,
    props.defaultValueShortName,
    props.defaultDirectionShortName,
  ]);

  const getRoseDataPrepare = useCallback(
    (rosePlotChart: RosePlotChart) => {
      if (variable == null || direction == null || isEmpty(rosePlotChart)) {
        return;
      }
      const axisX = rosePlotChart.Axises.find((axis) => axis.Type === 'X');
      setUnitX(axisX !== undefined ? axisX.Unit : '');
      const axisY = rosePlotChart.Axises.find((axis) => axis.Type === 'Y');
      const unitY = axisY !== undefined ? ' ' + axisY.Unit : '';
      const roseSeries = rosePlotChart.Ranges!.map((r) => r.Name + unitY);

      const roseData = Object.keys(rosePlotChart.Data!).map((category) => {
        const dataSet: { Name: string; Frequency: number }[] = (
          rosePlotChart.Data as any
        )[category];
        return dataSet.reduce(
          (a, b) => {
            return { ...a, [b.Name + unitY]: b.Frequency };
          },
          { category }
        );
      });
      setRosePlotSeries(roseSeries);
      setRosePlotData(roseData);
      setUpdatedRosePlotData(rosePlotChart)
    },
    [variable, direction]
  );

  useEffect(() => {
    previewsettingChanged &&
      message.warn('Please click Update once you finished your selection');
  }, [previewsettingChanged]);

  useEffect(() => {
    if (
      (!legendInterval || !directionNo) &&
      !previewsettingChanged &&
      variable !== undefined &&
      direction !== undefined &&
      variables.length > 0 &&
      directions.length > 0
    ) {
      getRoseDataPrepare(props.plotData);
      props.plotDefaultInterval !== undefined &&
        !isNaN(Number(props.plotDefaultInterval)) &&
        setLegendInterval(props.plotDefaultInterval);
      props.plotData &&
        props.plotData.Directions &&
        setDirectionNo(props.plotData.Directions.length);
    }
  }, [
    legendInterval,
    directionNo,
    variable,
    direction,
    variables,
    directions,
    props.plotData,
    props.plotDefaultInterval,
    previewsettingChanged,
  ]);

  useEffect(() => {
    if (props.updatedRosePlotData !== null) {
      getRoseDataPrepare(props.updatedRosePlotData);
      props.updatedRosePlotData.Directions &&
        setDirectionChartNo(props.updatedRosePlotData.Directions.length);
    }
  }, [props.updatedRosePlotData]);

  const onUpdateRosePlot = useCallback((dateRange?: any, directionNumber?: number, defaultInterval?: number) => {
    if (dataset && variable) {
      setDownloadName(
        getPointNameAndAcronym(dataset, datasetBriefs, point) +
        props.pointProps +
        '_' +
        formatDate(startDate) +
        '_' +
        formatDate(endDate) +
        '_Rose_Plot'
      );
      const brief = datasetBriefs.find((brief: IDatasetBrief) => brief.Id === dataset)
      const isFwe = brief?.IsFWE
      const values: IGetRosePlotV2ApiPayload = {
        datasetName: dataset,
        parameters: '',
        startDate: dateRange ? dateRange[0].format(DT0Format) : startDate.format(DT0Format),
        endDate: dateRange ? dateRange[1].format(DT0Format) : endDate.format(DT0Format),
        month: month,
        degreeInterval: directionNumber ?? directionNo,
        legendInterval: defaultInterval ?? legendInterval,
        valueColumnName: variable.Name,
        directionColumnName: direction?.Name,
        isFwe: isFwe
      };
      props.onUpdateRosePlot(values);
    }
  }, [
    startDate,
    endDate,
    dataset,
    variable,
    legendInterval,
    direction,
    directionNo,
    month,
    props,
    datasetBriefs,
    point,
    props.plotData
  ]);

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(props.previewDatasetTimeRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(props.previewDatasetTimeRange[1], 'day');
  }

  const memoCoords = useMemo(() => {
    return SplitCoordinates(props.pointProps);
  }, [props.pointProps]);

  const categories = useMemo(() => {
    const label = props.datasets.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [props.datasets])

  useEffect(() => {
    if (isForAllAnalytics && dataset && variable && directionNo && legendInterval) {
      if (selfTriggered.current) {
        selfTriggered.current = false;
        return;
      }
      if(globalDateRange[0] !== startDate || globalDateRange[1] !== endDate) {
        onUpdateRosePlot(globalDateRange, directionNo, legendInterval);
        setStartDate(globalDateRange[0]);
        setEndDate(globalDateRange[1]);
      }
    }
  }, [isForAllAnalytics, dataset, variable, directionNo, legendInterval]);

  return (
    <>
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={props.onChangeActiveKey}
                options={props.pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px'}}
                  onChange={onDatasetChange}
                  placeholder="Select dataset"
                  disabled={updating}
                >
                  {categories.map((category: string) => {
                    const section = category
                    return (
                      <Select.OptGroup key={category} label={section}>
                        {props.datasets
                          .filter((item) => {
                            return item.label === category;
                          })
                          .map((item) => {
                            return (
                              <Select.Option key={item.id} value={item.id}>
                                {item.value}
                              </Select.Option>
                            );
                          })}
                      </Select.OptGroup>
                    );
                  })}
                </Select>
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px' }}
                  placeholder="Select variable"
                  onChange={doVariableChange}
                  value={variable?.Index ? variable.Index : 0}
                  disabled={updating}
                >
                  {variables
                    .filter((item) => item.Name !== 'Time')
                    .map((item) => {
                      return (
                        <Select.Option key={item.Index} value={item.Index}>
                          {item.Name}
                        </Select.Option>
                      );
                  })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  className={styles.datePicker}
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  className={styles.datePicker}
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                />
              </Col>
              <Col span={24} md={5} className={styles.checkboxItem} >
                <Checkbox checked={isForAllAnalytics} onClick={handleCheckboxChange} />
                <Text onClick={handleCheckboxChange} style={{cursor: 'pointer'}}>Use this period for all analytics</Text>
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              disabled={updating}
              onClick={() => onUpdateRosePlot()}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
         {!updating && errorUpdate && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{errorUpdate}</div>}
         {!updating && !errorUpdate && (
          <div className={styles.chartPointWrapper}>
            <div className={styles.analyticsPointName}>
              {pointName}
              <span className={styles.analyticsCoords}>
                ({memoCoords.lng}°E; {memoCoords.lat}°N)
              </span>
            </div>
              <ChartBox
                actionRow={<></>}
                key={'Rose Plot'}
                title={'Rose Plot'}
                height={500}
                plot={
                  <RoseChart
                    series={rosePlotSeries}
                    data={rosePlotData}
                    angle={-360 / directionChartNo / 2 - 90}
                    unitX={unitX}
                    downloadName={downloadName}
                  />
                }
                dataset={props.datasets.find((d) => d.id === dataset)?.label || ''}
              />
            </div>
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
          }}
        >
          <Label label="Direction" className={styles.actionItem}>
          <Select
            value={direction?.Index ? direction.Index : 0}
            style={{ width: '100%' }}
            onChange={doDirectionChange}
            disabled={updating}
          >
            {directions.map((item) => {
              return (
                <Select.Option key={item.Index} value={item.Index}>
                  {item.Name}
                </Select.Option>
              );
            })}
          </Select>
          </Label>
          <Label label="No. of Directions" className={styles.actionItem}>
            <Select
              value={directionNo}
              style={{ width: '100%' }}
              onChange={doChangeDirectionNo}
              disabled={updating}
            >
              {degreeIntervals.map((interval) => {
                return (
                  <Select.Option key={interval} value={interval}>
                    {interval}
                  </Select.Option>
                );
              })}
            </Select>
          </Label>
          <Label label="Legend Intervals" className={styles.actionItem}>
            <InputNumber
              value={legendInterval}
              precision={2}
              min={0.01}
              onChange={doChangeIntervalNo}
              disabled={updating}
              style={{ width: '100%' }}
            />
          </Label>
        </Sider>
      </Layout>
    </Layout>
    </>
  )
}
